import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addToFocus,
  endFocusSession,
  getFocusSession,
  removeFromFocus,
  type FocusSession,
  type FocusSummary,
  type FocusTask,
} from '../../lib/focus';
import {
  applyRowPatch,
  getRunningTimer,
  startTimer,
  stopTimer,
  updateTask,
  type RowPatch,
  type Task,
  type TaskSummary,
} from '../../lib/tasks';
import { putTask } from '../../lib/taskCache';
import { statusChangedMessage } from '../../lib/taskStatus';
import { useToast } from '../toast';

/**
 * The id an optimistic session carries until the server answers with a real one.
 *
 * Prefixed the way a pending task id is, and for the same reason: a real id is
 * always a uuid, so nothing can mistake this for one the API would recognise.
 * Nothing sends it anywhere either -- every endpoint here reads the caller's own
 * session rather than being told which one to touch.
 */
const PENDING_SESSION_ID = 'pending:session';

/**
 * The current sitting -- the data half of FocusCard.
 *
 * A sibling of useDayTasks, and built the same way for the same reasons: the
 * task panel opens *over* the dashboard, so an edit made in the panel has to
 * reach the row sitting behind it, and the page owns the rows so the card and
 * the panel share one copy rather than two that drift.
 *
 * Where it deliberately differs from useDayTasks is completion. The day card
 * drops a task the moment it is done, because a card headed "Due" should not
 * list finished work. This one keeps it, ticked: the score -- three of four --
 * is the entire point of having picked them.
 */
export function useFocusSession({
  onTaskChanged,
}: { onTaskChanged?: (task: Task) => void } = {}) {
  const [session, setSession] = useState<FocusSession | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Two error slots, the same split useDayTasks explains: a failed *load* has no
   * card to show and stands in for one, while a failed *edit* still has the card
   * -- and the row you clicked is in it -- so replacing it with a red sentence
   * takes away the thing you were looking at.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Which task the caller's single timer is on, if any. App-wide, so it can
   *  point at a task that is not in this session at all. */
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
  /** The row whose *timer* is mid-request. Picking and unpicking are optimistic
   *  and disable nothing; starting a clock is not, because the server keeps one
   *  running entry per user and a second click before the first lands is how you
   *  end up with the hour on the wrong task. */
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  /** A `clearDone` sweep is in flight. Its own flag rather than `busyTaskId`,
   *  which names a single row: this one belongs to the header button. */
  const [clearing, setClearing] = useState(false);

  /** One counter per task, so a slow reply to an earlier click cannot overwrite
   *  a later one -- the guard every optimistic list in this app keeps. */
  const patchStamps = useRef(new Map<string, number>());
  /** The session as it stands, so the callbacks below can read it without being
   *  rebuilt -- and handed to the panel -- every time it changes. */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  /** Read through a ref for the same reason: `patch` is built once, and the
   *  page's callback is rebuilt whenever the other list changes. */
  const changedRef = useRef(onTaskChanged);
  changedRef.current = onTaskChanged;
  /** For `patch`, which is built once and raises the Undo toast. */
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([getFocusSession(controller.signal), getRunningTimer(controller.signal)])
      .then(([focus, timer]) => {
        setSession(focus.session);
        setRunningTaskId(timer.entry?.taskId ?? null);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load your session.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  /** What the day list reads to mark what is already picked -- a set rather than
   *  a scan, since every day row asks. */
  const taskIds = useMemo(
    () => new Set((session?.tasks ?? []).map((task) => task.id)),
    [session],
  );

  /**
   * The picks in flight: ordered per task, counted as a whole.
   *
   * Two rules, because a click nobody waits for can race in two directions.
   *
   * Per task, requests run one after another. Picking something and putting it
   * straight back are a POST and a DELETE for the same row, and a DELETE that
   * overtakes its POST leaves the server holding a pick nobody wanted -- which
   * you would only find out about tomorrow, when the session loads with it in.
   *
   * Across tasks, a reply is only taken when it is the last word. Every reply
   * carries the whole session, so an older one landing late would wipe out a
   * pick made while it was still in the air. When it is not the last word the
   * local copy stands, which is the honest thing: it is the sum of every change
   * that has been asked for, and each failure undoes its own.
   */
  const picks = useRef({
    issued: 0,
    inFlight: 0,
    chains: new Map<string, Promise<unknown>>(),
  });

  /**
   * Send one membership change and reconcile what comes back.
   *
   * The caller has already changed the card -- that is the point -- so this has
   * only two jobs: take the server's session when it is safe to, which is what
   * quietly turns an optimistic session into a real one, id and start time and
   * all; and hand the change back to `undo` when the request fails.
   */
  const sendPick = useCallback(
    (
      taskId: string,
      send: () => Promise<{ session: FocusSession }>,
      undo: () => void,
      whenItFails: string,
    ): Promise<void> => {
      const state = picks.current;
      const stamp = ++state.issued;
      state.inFlight += 1;

      // The previous link ends in the catch below and so never rejects, which
      // is what lets this wait on it without a guard of its own.
      const settled = (state.chains.get(taskId) ?? Promise.resolve())
        .then(send)
        .then(({ session: updated }) => {
          // `inFlight === 1` is this request itself: the decrement below has
          // not run yet, so one means there is nothing else out there.
          if (stamp === state.issued && state.inFlight === 1) setSession(updated);
        })
        .catch((requestError: unknown) => {
          undo();
          setActionError(requestError instanceof Error ? requestError.message : whenItFails);
        })
        .finally(() => {
          state.inFlight -= 1;
          // Only if nothing has queued behind it, or the next click would wait
          // on a link that has already run.
          if (state.chains.get(taskId) === settled) state.chains.delete(taskId);
        });

      state.chains.set(taskId, settled);
      return settled;
    },
    [],
  );

  /**
   * Pick a task, starting a session if this is the first one.
   *
   * Optimistic, like everything else on these two cards. It used to wait for the
   * reply -- there was nothing on screen to update in place, and the reply is
   * the whole session -- but that made the *first* pick the worst one: the card
   * has no session until the server says so, so a click on a day row left the
   * page sitting still for a round trip to Frankfurt and back before anything
   * moved. Now the pick lands in the same paint and the request follows it.
   *
   * The optimistic session is session enough to render: a placeholder id that
   * goes nowhere near the API, and a start time a second or so ahead of the one
   * the server will stamp. The reply replaces both.
   */
  const add = useCallback(
    async (task: TaskSummary) => {
      // A second click on something already picked is a double-click, not an
      // add -- the same call the server makes about the same request.
      if (sessionRef.current?.tasks.some((entry) => entry.id === task.id)) return;

      const now = new Date().toISOString();
      // `completedAt` belongs to the pick, not to the task: a pick starts
      // unticked whatever state the task is in, which is what the server writes.
      const pick: FocusTask = { ...task, addedAt: now, completedAt: null };

      setSession((current) =>
        current === null
          ? { id: PENDING_SESSION_ID, startedAt: now, tasks: [pick] }
          : // Appended, because the server hands its picks back oldest first.
            { ...current, tasks: [...current.tasks, pick] },
      );
      setActionError(null);

      await sendPick(
        task.id,
        () => addToFocus(task.id),
        () =>
          setSession((current) => {
            if (current === null) return current;
            const tasks = current.tasks.filter((entry) => entry.id !== task.id);
            // The first pick *is* the session, so losing it takes the session
            // with it: there is no session the server ever opened, and the card
            // goes back to being one line.
            return tasks.length === 0 && current.id === PENDING_SESSION_ID
              ? null
              : { ...current, tasks };
          }),
        'Could not add that to your session.',
      );
    },
    [sendPick],
  );

  /** Put one back on the pile. Optimistic for the reason `add` is, and the
   *  captured index puts it back where it was if the request fails. */
  const remove = useCallback(
    async (taskId: string) => {
      const current = sessionRef.current;
      if (!current) return;
      const index = current.tasks.findIndex((task) => task.id === taskId);
      const removed = current.tasks[index];
      if (!removed) return;

      setSession((latest) =>
        latest === null
          ? latest
          : { ...latest, tasks: latest.tasks.filter((task) => task.id !== taskId) },
      );
      setActionError(null);

      await sendPick(
        taskId,
        () => removeFromFocus(taskId),
        () =>
          setSession((latest) =>
            latest === null
              ? latest
              : {
                  ...latest,
                  tasks: [...latest.tasks.slice(0, index), removed, ...latest.tasks.slice(index)],
                },
          ),
        'Could not put that back.',
      );
    },
    [sendPick],
  );

  /**
   * Take the finished picks off the list, all at once.
   *
   * The same act as clicking every ticked row's undo button, and that is exactly
   * how it is built: `remove` per task rather than a bulk endpoint. The ticked
   * half of a twenty-pick session is a handful of DELETEs, `sendPick` already
   * serialises them per task and ignores all but the last reply, and each one
   * that fails puts its own row back where it was. A `/api/focus/done` route
   * would be a second way to say the same thing with a second optimistic path to
   * keep in step with this one.
   *
   * What it costs is the tally. A cleared pick is gone from the session, so it
   * stops counting towards the "3 of 4 done" that `end` reports. That is the
   * bargain the button offers: the finished rows have stopped being worth
   * looking at, and the score goes with them.
   */
  const clearDone = useCallback(async () => {
    const done = (sessionRef.current?.tasks ?? []).filter((task) => task.status === 'DONE');
    if (done.length === 0) return;

    setClearing(true);
    try {
      // `remove` settles rather than rejects -- sendPick catches -- so one
      // failure among them still lets the rest finish and the button settle.
      await Promise.all(done.map((task) => remove(task.id)));
    } finally {
      setClearing(false);
    }
  }, [remove]);

  /**
   * One inline status change, applied optimistically.
   *
   * Unlike the day card's, DONE keeps its row -- see the note on this hook. The
   * pick's own `completedAt` is mirrored alongside the task's, because that is
   * what the card counts, and waiting for the round trip would leave the header
   * reading "1 of 4" for a moment after you ticked the second one off.
   */
  const patch = useCallback(async (
    task: TaskSummary,
    changes: RowPatch,
    /** False for the Undo itself, which must not offer to undo itself. */
    { undoable = true }: { undoable?: boolean } = {},
  ) => {
    const sent = (patchStamps.current.get(task.id) ?? 0) + 1;
    patchStamps.current.set(task.id, sent);

    const stampedAt =
      changes.status === undefined || changes.status === task.status
        ? undefined
        : changes.status === 'DONE'
          ? new Date().toISOString()
          : null;

    const write = (next: TaskSummary, completedAt: string | null | undefined) =>
      setSession((current) =>
        current === null
          ? current
          : {
              ...current,
              tasks: current.tasks.map((entry) =>
                entry.id === task.id
                  ? {
                      ...entry,
                      ...next,
                      ...(completedAt === undefined ? {} : { completedAt }),
                    }
                  : entry,
              ),
            },
      );

    write(applyRowPatch(task, changes), stampedAt);
    setActionError(null);

    try {
      const { task: updated, spawned } = await updateTask(task.id, changes);
      if (patchStamps.current.get(task.id) !== sent) return;
      write(updated, stampedAt);
      putTask(updated);
      // The day card may be holding this same task -- see the note on the
      // dashboard's `crossSync`. Ticking something off here is exactly the case
      // that matters: it stops belonging on a list of what is still due.
      changedRef.current?.(updated);

      // On the reply, not the click: a change that failed has already been put
      // back, and offering to undo it would be a lie.
      if (undoable && changes.status !== undefined && changes.status !== task.status) {
        const from = changes.status;
        const to = task.status;
        toastRef.current(statusChangedMessage(task.title, from, spawned), {
          action: {
            label: 'Undo',
            onAction: () => {
              // Not if the pick has been cleared off the list since, and not if
              // something has moved it again -- that is the newer word.
              const current = sessionRef.current?.tasks.find((entry) => entry.id === task.id);
              if (!current || current.status !== from) return;
              void patchRef.current(current, { status: to }, { undoable: false });
            },
          },
        });
      }
    } catch (updateError: unknown) {
      if (patchStamps.current.get(task.id) !== sent) return;
      // Back to the row as it was, pick stamp and all.
      const before = sessionRef.current?.tasks.find((entry) => entry.id === task.id);
      write(task, before?.completedAt ?? null);
      setActionError(
        updateError instanceof Error ? updateError.message : 'Could not update this task.',
      );
    }
  }, []);
  // The Undo calls back into `patch`, which is built once and cannot name
  // itself in its own initializer's dependencies.
  const patchRef = useRef(patch);
  patchRef.current = patch;

  /**
   * Start or stop the timer on one row.
   *
   * The server keeps one running entry per user app-wide and answers a second
   * start with a 409 rather than switching, so moving the timer to another task
   * means stopping the first one here. That is also the behaviour you want: a
   * session is one pair of hands, and two running timers would double-count the
   * hour.
   */
  const toggleTimer = useCallback(
    async (taskId: string) => {
      setBusyTaskId(taskId);
      setActionError(null);
      try {
        if (runningTaskId === taskId) {
          await stopTimer(taskId);
          setRunningTaskId(null);
          return;
        }
        if (runningTaskId) await stopTimer(runningTaskId);
        await startTimer(taskId);
        setRunningTaskId(taskId);
      } catch (timerError: unknown) {
        setActionError(
          timerError instanceof Error ? timerError.message : 'Could not change the timer.',
        );
        // The local guess may now be wrong either way -- re-ask rather than
        // leave a Stop button on a timer that never started.
        void getRunningTimer()
          .then((timer) => setRunningTaskId(timer.entry?.taskId ?? null))
          .catch(() => {});
      } finally {
        setBusyTaskId(null);
      }
    },
    [runningTaskId],
  );

  /** Close the session and hand back the summary for the caller to announce.
   *  Returns null if there was nothing to end, which a double-click produces. */
  const end = useCallback(async (): Promise<FocusSummary | null> => {
    setEnding(true);
    setActionError(null);
    try {
      // Picks still in the air are waited out first. Now that adding does not
      // block the click, "pick something, then end the session" can be two
      // requests in the same second -- and a POST that lands after this one
      // would open a fresh session holding whatever it was picking. Only ever a
      // wait when something is genuinely in flight, and the button says so.
      await Promise.all([...picks.current.chains.values()]);
      const { summary } = await endFocusSession();
      setSession(null);
      return summary;
    } catch (endError: unknown) {
      setActionError(
        endError instanceof Error ? endError.message : 'Could not end the session.',
      );
      return null;
    } finally {
      setEnding(false);
    }
  }, []);

  /**
   * A task the panel changed, folded back into the card behind it.
   *
   * Updates in place and never inserts, for the reason useDayTasks gives: the
   * panel can be opened on a task that was never picked, and a card headed
   * "Focus" quietly growing a row for one would be a lie about what you chose.
   * Unlike the day card's, a completed task is kept rather than dropped.
   */
  const syncTask = useCallback((task: Task) => {
    setSession((current) =>
      current === null || !current.tasks.some((entry) => entry.id === task.id)
        ? current
        : {
            ...current,
            tasks: current.tasks.map((entry) =>
              entry.id === task.id
                ? {
                    ...entry,
                    ...task,
                    // The pick's stamp is the session's to keep -- the panel does
                    // not know about it, and the server has already recorded it.
                    completedAt:
                      task.status === 'DONE'
                        ? (entry.completedAt ?? new Date().toISOString())
                        : null,
                  }
                : entry,
            ),
          },
    );
  }, []);

  /** A task deleted from the panel. The server cascades the pick away; this is
   *  the card catching up without a refetch. */
  const removeTask = useCallback((taskId: string) => {
    setSession((current) =>
      current === null
        ? current
        : { ...current, tasks: current.tasks.filter((entry) => entry.id !== taskId) },
    );
  }, []);

  return {
    session,
    taskIds,
    loading,
    loadError,
    actionError,
    setActionError,
    runningTaskId,
    busyTaskId,
    ending,
    clearing,
    add,
    remove,
    clearDone,
    patch,
    toggleTimer,
    end,
    syncTask,
    removeTask,
  };
}

/** What the card renders and what the panel writes back through, named once so
 *  neither has to restate the hook's shape. */
export type FocusSessionState = ReturnType<typeof useFocusSession>;
